import {
  ChatMessage,
  KeyMetric,
  FinancialInsight,
  RecentActivity,
  ERPConnection,
  FinancialData,
  TimeSeriesData
} from '../types';

export const mockFinancialData: FinancialData = {
  // Monthly revenue data
  revenue: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Revenue 2024',
        data: [65000, 72000, 81000, 76000, 85000, 92000, 97000, 103000, 109000, 112000, 118000, 125000],
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
      },
      {
        label: 'Revenue 2023',
        data: [58000, 63000, 69000, 66000, 71000, 76000, 82000, 89000, 93000, 97000, 101000, 108000],
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
      }
    ]
  },
  
  // Expenses by category
  expenses: {
    labels: ['Payroll', 'Marketing', 'Operations', 'Technology', 'Office', 'Travel'],
    datasets: [
      {
        label: 'Expenses',
        data: [450000, 120000, 180000, 95000, 75000, 40000],
        backgroundColor: [
          'rgba(255, 99, 132, 0.6)',
          'rgba(54, 162, 235, 0.6)',
          'rgba(255, 206, 86, 0.6)',
          'rgba(75, 192, 192, 0.6)',
          'rgba(153, 102, 255, 0.6)',
          'rgba(255, 159, 64, 0.6)'
        ],
        borderWidth: 1,
      }
    ]
  },
  
  // Profit margins by product
  profitMargins: {
    labels: ['Product A', 'Product B', 'Product C', 'Product D', 'Product E'],
    datasets: [
      {
        label: 'Profit Margin (%)',
        data: [32, 45, 28, 38, 52],
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1,
      }
    ]
  },
  
  // Cash flow forecast
  cashFlowForecast: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Forecasted Cash Flow',
        data: [42000, 48000, 52000, 59000, 65000, 73000],
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderDash: [5, 5],
      },
      {
        label: 'Actual Cash Flow',
        data: [45000, 51000, 54000, 60000, null, null],
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
      }
    ]
  }
};

export const mockFinancialInsights: FinancialInsight[] = [
  {
    id: 1,
    title: "Revenue Growth Opportunity",
    description: "Product C shows 28% monthly growth potential based on market analysis and current trend data.",
    impact: "High",
    category: "Revenue"
  },
  {
    id: 2,
    title: "Cost Reduction Alert",
    description: "Travel expenses increased 14% above quarterly average. Consider implementing stricter approval processes.",
    impact: "Medium",
    category: "Expenses"
  },
  {
    id: 3,
    title: "Cash Flow Warning",
    description: "Projected cash flow deficit in Q3 based on current accounts receivable aging and payment patterns.",
    impact: "High",
    category: "Cash Flow"
  },
  {
    id: 4,
    title: "Operational Efficiency",
    description: "Operations cost per unit has decreased by 5% after implementing new inventory management system.",
    impact: "Medium",
    category: "Operations"
  },
  {
    id: 5,
    title: "Profit Margin Alert",
    description: "Product B margin increased to 45%, making it the most profitable product line. Consider scaling production.",
    impact: "High",
    category: "Profit"
  }
];

export const mockRecentActivity: RecentActivity[] = [
  {
    id: 1,
    action: "Data import",
    description: "Q3 Sales data imported",
    timestamp: "2024-05-14T10:23:45",
    user: "John Smith"
  },
  {
    id: 2,
    action: "Analysis",
    description: "Revenue forecast generated",
    timestamp: "2024-05-14T09:15:22",
    user: "Maria Garcia"
  },
  {
    id: 3,
    action: "Data transformation",
    description: "Cleaned customer segmentation data",
    timestamp: "2024-05-13T16:42:10",
    user: "John Smith"
  },
  {
    id: 4,
    action: "Report",
    description: "Monthly financial summary created",
    timestamp: "2024-05-13T15:30:55",
    user: "Sarah Johnson"
  },
  {
    id: 5,
    action: "ERP Integration",
    description: "SAP sales data synchronized",
    timestamp: "2024-05-13T11:05:33",
    user: "Ahmed Hassan"
  }
];

export const mockKeyMetrics: KeyMetric[] = [
  {
    id: 1,
    name: "Revenue (MTD)",
    value: "$1.25M",
    change: "+8.5%",
    positive: true
  },
  {
    id: 2,
    name: "Operating Expenses",
    value: "$425K",
    change: "-3.2%",
    positive: true
  },
  {
    id: 3,
    name: "Profit Margin",
    value: "32.4%",
    change: "+2.1%",
    positive: true
  },
  {
    id: 4,
    name: "Cash Reserve",
    value: "$3.8M",
    change: "+5.7%",
    positive: true
  },
  {
    id: 5,
    name: "Customer Acquisition Cost",
    value: "$1,250",
    change: "+1.8%",
    positive: false
  }
];

export const mockTimeSeriesData: TimeSeriesData = {
  historical: [
    { date: '2024-01-01', value: 105000 },
    { date: '2024-01-15', value: 107000 },
    { date: '2024-02-01', value: 112000 },
    { date: '2024-02-15', value: 115000 },
    { date: '2024-03-01', value: 118000 },
    { date: '2024-03-15', value: 120000 },
    { date: '2024-04-01', value: 123000 },
    { date: '2024-04-15', value: 126000 },
    { date: '2024-05-01', value: 129000 },
  ],
  forecast: [
    { date: '2024-05-15', value: 132000 },
    { date: '2024-06-01', value: 135000 },
    { date: '2024-06-15', value: 138000 },
    { date: '2024-07-01', value: 141000 },
    { date: '2024-07-15', value: 145000 },
    { date: '2024-08-01', value: 148000 },
  ]
};

export const mockChatHistory: ChatMessage[] = [
  {
    id: 1,
    role: "user",
    content: "Show me revenue trends for Q1 2024",
    timestamp: "2024-05-14T09:30:22"
  },
  {
    id: 2,
    role: "assistant",
    content: "I've analyzed the Q1 2024 revenue data. Overall, revenue increased by 12.3% compared to Q1 2023. Here's the monthly breakdown:\n\n- January: $105,000 (+8.2% YoY)\n- February: $112,000 (+11.4% YoY)\n- March: $118,000 (+15.6% YoY)\n\nThe strongest growth was in the enterprise segment, which saw a 18.7% increase. Would you like me to generate a detailed report or visualize this data?",
    timestamp: "2024-05-14T09:30:30"
  },
  {
    id: 3,
    role: "user",
    content: "Compare profit margins across product lines",
    timestamp: "2024-05-14T09:31:05"
  },
  {
    id: 4,
    role: "assistant",
    content: "Here's a comparison of profit margins across product lines:\n\n- Product A: 32% (-1.2% from last quarter)\n- Product B: 45% (+3.8% from last quarter)\n- Product C: 28% (no change)\n- Product D: 38% (+2.1% from last quarter)\n- Product E: 52% (+5.3% from last quarter)\n\nProduct E shows the highest profit margin at 52%, with the largest increase this quarter. Product B also performed well with a 3.8% increase. Would you like a deeper analysis of any specific product line?",
    timestamp: "2024-05-14T09:31:15"
  }
];

export const mockERPConnections: ERPConnection[] = [
  {
    id: 1,
    system: "SAP S/4HANA",
    status: "Connected",
    lastSync: "2024-05-14T08:30:00",
    entities: ["Customers", "Products", "Sales", "Invoices"]
  },
  {
    id: 2,
    system: "Oracle ERP Cloud",
    status: "Disconnected",
    lastSync: "2024-05-10T14:15:22",
    entities: ["Financials", "Procurement", "Projects"]
  },
  {
    id: 3,
    system: "Microsoft Dynamics 365",
    status: "Connected",
    lastSync: "2024-05-13T16:45:10",
    entities: ["Sales", "Customers", "Inventory"]
  }
];
